import React, { useState, useRef } from 'react';
import '../styles/home.css';
import { PDFDocument } from 'pdf-lib';
import addLogoIcon from '../assets/add-logo-icon.svg';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const AddLogoToPdf = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [logoLibrary, setLogoLibrary] = useState([]);
  const [placedLogos, setPlacedLogos] = useState([]);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pagePreview, setPagePreview] = useState(null);
  const [submitValue, setSubmitValue] = useState('Download PDF');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(100);
  const [showLibrary, setShowLibrary] = useState(true);
  const [resultBlob, setResultBlob] = useState(null);
  const containerRef = useRef(null);
  const pdfInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [draggingLogo, setDraggingLogo] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [scale, setScale] = useState(1);
  const pdfDocRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setPdfFile(null);
    setLogoLibrary([]);
    setPlacedLogos([]);
    setSelectedLogo(null);
    setCurrentPage(1);
    setTotalPages(0);
    setPagePreview(null);
    setSubmitValue('Download PDF');
    setNotify('');
    setError(null);
    setStep(1);
    setZoom(100);
    setResultBlob(null);
    if (pdfInputRef.current) pdfInputRef.current.value = null;
    if (logoInputRef.current) logoInputRef.current.value = null;
  };

  const handlePdfChange = async (event) => {
    const file = event.target.files[0];
    if (!file || file.type !== constants.pdfFileType) {
      setNotify('Please select a valid PDF file');
      setError('error');
      return;
    }

    setPdfFile(file);
    setNotify('');
    setError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      pdfDocRef.current = pdf;
      setTotalPages(pdf.numPages);
      setStep(2);
      await renderPage(pdf, 1);
    } catch (err) {
      setNotify('Failed to load PDF');
      setError('error');
    }
  };

  const renderPage = async (pdfDoc, pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: 1 });
    const containerWidth = 450;
    const newScale = containerWidth / viewport.width;
    setScale(newScale);
    const scaledViewport = page.getViewport({ scale: newScale * (zoom / 100) });
    setPageDimensions({ width: scaledViewport.width, height: scaledViewport.height });

    const canvas = document.createElement('canvas');
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: scaledViewport }).promise;
    setPagePreview(canvas.toDataURL());
  };

  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages || !pdfDocRef.current) return;
    setCurrentPage(newPage);
    await renderPage(pdfDocRef.current, newPage);
  };

  const handleZoom = async (delta) => {
    const newZoom = Math.max(50, Math.min(200, zoom + delta));
    setZoom(newZoom);
    if (pdfDocRef.current) await renderPage(pdfDocRef.current, currentPage);
  };

  const handleLogoUpload = (event) => {
    const files = Array.from(event.target.files);
    if (logoLibrary.length + files.length > 50) {
      setNotify('Maximum 50 logos allowed');
      return;
    }

    files.forEach((file) => {
      if (!file.type.match(/image\/(png|jpeg|jpg)/i)) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoLibrary((prev) => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            src: e.target.result,
            name: file.name,
            file,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
    if (logoInputRef.current) logoInputRef.current.value = null;
  };

  const addLogoToPdf = (logo) => {
    const newLogo = {
      id: Date.now() + Math.random(),
      logoId: logo.id,
      src: logo.src,
      name: logo.name,
      file: logo.file,
      x: pageDimensions.width / 2 - 50,
      y: pageDimensions.height / 2 - 50,
      width: 100,
      height: 100,
      opacity: 80,
      size: 30,
      applyTo: 'all',
      customRange: '',
      page: currentPage,
    };
    setPlacedLogos((prev) => [...prev, newLogo]);
    setSelectedLogo(newLogo.id);
    setStep(3);
  };

  const removeFromLibrary = (id) => {
    setLogoLibrary((prev) => prev.filter((l) => l.id !== id));
    setPlacedLogos((prev) => prev.filter((l) => l.logoId !== id));
  };

  const removePlacedLogo = (id) => {
    setPlacedLogos((prev) => prev.filter((l) => l.id !== id));
    if (selectedLogo === id) setSelectedLogo(null);
  };

  const updateLogo = (id, updates) => {
    setPlacedLogos((prev) => prev.map((l) => (l.id === id ? { ...l, ...updates } : l)));
  };

  const centerLogo = () => {
    if (!selectedLogo) return;
    const logo = placedLogos.find((l) => l.id === selectedLogo);
    if (logo) {
      updateLogo(selectedLogo, {
        x: (pageDimensions.width - logo.width) / 2,
        y: (pageDimensions.height - logo.height) / 2,
      });
    }
  };

  const handleMouseDown = (e, logo) => {
    e.preventDefault();
    setSelectedLogo(logo.id);
    const rect = containerRef.current.getBoundingClientRect();
    setDraggingLogo({ ...logo, offsetX: e.clientX - rect.left - logo.x, offsetY: e.clientY - rect.top - logo.y });
  };

  const handleResizeStart = (e, logo, corner) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedLogo(logo.id);
    const rect = containerRef.current.getBoundingClientRect();
    setResizing({
      id: logo.id,
      corner,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: logo.width,
      startHeight: logo.height,
      startLogoX: logo.x,
      startLogoY: logo.y,
    });
  };

  const handleMouseMove = (e) => {
    if (resizing && containerRef.current) {
      const deltaX = e.clientX - resizing.startX;
      const deltaY = e.clientY - resizing.startY;
      let newWidth = resizing.startWidth;
      let newHeight = resizing.startHeight;
      let newX = resizing.startLogoX;
      let newY = resizing.startLogoY;

      if (resizing.corner.includes('e')) newWidth = Math.max(30, resizing.startWidth + deltaX);
      if (resizing.corner.includes('w')) {
        newWidth = Math.max(30, resizing.startWidth - deltaX);
        newX = resizing.startLogoX + deltaX;
      }
      if (resizing.corner.includes('s')) newHeight = Math.max(30, resizing.startHeight + deltaY);
      if (resizing.corner.includes('n')) {
        newHeight = Math.max(30, resizing.startHeight - deltaY);
        newY = resizing.startLogoY + deltaY;
      }

      setPlacedLogos((prev) =>
        prev.map((l) => (l.id === resizing.id ? { ...l, width: newWidth, height: newHeight, x: newX, y: newY } : l)),
      );
      return;
    }

    if (!draggingLogo || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(
      0,
      Math.min(e.clientX - rect.left - draggingLogo.offsetX, pageDimensions.width - draggingLogo.width),
    );
    const y = Math.max(
      0,
      Math.min(e.clientY - rect.top - draggingLogo.offsetY, pageDimensions.height - draggingLogo.height),
    );
    setPlacedLogos((prev) => prev.map((l) => (l.id === draggingLogo.id ? { ...l, x, y } : l)));
  };

  const handleMouseUp = () => {
    setDraggingLogo(null);
    setResizing(null);
  };

  const applyLogos = async () => {
    if (!pdfFile || placedLogos.length === 0) return;
    setSubmitValue(<Spinner />);

    try {
      const pdfBytes = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      // Helper to convert base64 to Uint8Array
      const base64ToUint8Array = (base64) => {
        const base64Data = base64.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      for (const logo of placedLogos) {
        const imageBytes = base64ToUint8Array(logo.src);
        const image = logo.file.type.includes('png')
          ? await pdfDoc.embedPng(imageBytes)
          : await pdfDoc.embedJpg(imageBytes);

        let targetPages = [];
        if (logo.applyTo === 'all') {
          targetPages = pages.map((_, i) => i);
        } else if (logo.applyTo === 'current') {
          targetPages = [logo.page - 1];
        } else if (logo.applyTo === 'custom' && logo.customRange) {
          logo.customRange.split(',').forEach((part) => {
            const [start, end] = part.split('-').map((n) => parseInt(n.trim()) - 1);
            if (end !== undefined) {
              for (let i = start; i <= end && i < pages.length; i++) targetPages.push(i);
            } else if (start < pages.length) targetPages.push(start);
          });
        }

        for (const pageIdx of targetPages) {
          const page = pages[pageIdx];
          if (!page) continue;
          const { height: pageHeight } = page.getSize();
          const zoomFactor = zoom / 100;
          const pdfX = logo.x / (scale * zoomFactor);
          const pdfY = pageHeight - logo.y / (scale * zoomFactor) - logo.height / (scale * zoomFactor);

          page.drawImage(image, {
            x: pdfX,
            y: pdfY,
            width: logo.width / (scale * zoomFactor),
            height: logo.height / (scale * zoomFactor),
            opacity: logo.opacity / 100,
          });
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: constants.pdfFileType });
      setResultBlob(blob);
      setSubmitValue('Download PDF');
      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
      setStep(4);
    } catch (err) {
      console.error('Error adding logos:', err);
      setNotify('Failed to add logos. Please try again.');
      setError('error');
      setSubmitValue('Download PDF');
    }
  };

  const downloadResult = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logo_${pdfFile.name}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const currentPageLogos = placedLogos.filter((l) => l.applyTo === 'all' || l.page === currentPage);
  const selectedLogoData = placedLogos.find((l) => l.id === selectedLogo);

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <img src={addLogoIcon} alt="Add Logo" loading="lazy" />
        </div>
        <h3 className="service-card-title">Add Logo to PDF</h3>
        <p className="service-card-desc">Add logos/watermarks to all or selected pages at once</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="max"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {step === 3 && placedLogos.length > 0 && (
                <Button variant="primary" onClick={applyLogos}>
                  {submitValue}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="ADD MULTIPLE LOGOS TO PDF"
      >
        <div>
          {/* Progress Steps */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
            }}
          >
            {['Upload PDF', 'Add Logos', 'Place & Edit', 'Download'].map((label, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: step > i + 1 ? '#52c41a' : step === i + 1 ? '#FF9900' : '#ddd',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  {i + 1}
                </div>
                <span
                  style={{
                    fontSize: '13px',
                    fontWeight: step === i + 1 ? '600' : '400',
                    color: step >= i + 1 ? '#333' : '#999',
                  }}
                >
                  {label}
                </span>
                {i < 3 && <span style={{ color: '#ccc', margin: '0 8px' }}>→</span>}
              </div>
            ))}
          </div>

          {step < 4 ? (
            <div style={{ display: 'flex', gap: '20px' }}>
              {/* Left Panel - Library */}
              <div style={{ width: '250px', flexShrink: 0 }}>
                <div className="bordered-box">
                  <h6 className="container-header">📄 Choose PDF File</h6>
                  <FormField errorText={notify}>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfChange}
                      style={{ width: '100%' }}
                    />
                  </FormField>
                  {pdfFile && (
                    <div style={{ marginTop: '8px', color: '#52c41a', fontSize: '13px' }}>
                      📄 {pdfFile.name} ({totalPages} pages)
                    </div>
                  )}
                </div>

                {step >= 2 && (
                  <div className="bordered-box">
                    <h6 className="container-header">📂 Logo Library ({logoLibrary.length}/50)</h6>
                    <div style={{ marginBottom: '12px' }}>
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          backgroundColor: '#FF9900',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        ➕ Add Logo
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept=".png,.jpg,.jpeg"
                          multiple
                          onChange={handleLogoUpload}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {logoLibrary.map((logo) => (
                        <div
                          key={logo.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px',
                            backgroundColor: '#fafafa',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            border: '1px solid #eee',
                          }}
                        >
                          <img
                            src={logo.src}
                            alt=""
                            style={{
                              width: '40px',
                              height: '40px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              backgroundColor: '#fff',
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {logo.name}
                            </div>
                            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                              <button
                                onClick={() => addLogoToPdf(logo)}
                                style={{
                                  padding: '2px 6px',
                                  fontSize: '10px',
                                  backgroundColor: '#52c41a',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                }}
                              >
                                Add to PDF
                              </button>
                              <button
                                onClick={() => removeFromLibrary(logo.id)}
                                style={{
                                  padding: '2px 6px',
                                  fontSize: '10px',
                                  backgroundColor: '#ff4d4f',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Center - PDF Preview */}
              <div style={{ flex: 1 }}>
                {pagePreview && (
                  <div className="bordered-box">
                    {/* Toolbar */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '6px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ◀
                        </button>
                        <span style={{ fontSize: '13px' }}>
                          Page {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ▶
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={() => handleZoom(-10)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ➖
                        </button>
                        <span style={{ fontSize: '13px', minWidth: '40px', textAlign: 'center' }}>{zoom}%</span>
                        <button
                          onClick={() => handleZoom(10)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                          }}
                        >
                          ➕
                        </button>
                        <button
                          onClick={() => {
                            setZoom(100);
                            if (pdfDocRef.current) renderPage(pdfDocRef.current, currentPage);
                          }}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: '#fff',
                            fontSize: '12px',
                          }}
                        >
                          Fit
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={centerLogo}
                          disabled={!selectedLogo}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: selectedLogo ? 'pointer' : 'not-allowed',
                            backgroundColor: '#fff',
                            fontSize: '12px',
                          }}
                        >
                          📍 Center
                        </button>
                        <button
                          onClick={() => setShowLibrary(!showLibrary)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: showLibrary ? '#e6f7ff' : '#fff',
                            fontSize: '12px',
                          }}
                        >
                          📂 Library
                        </button>
                      </div>
                    </div>

                    {/* PDF Canvas */}
                    <div
                      style={{ overflow: 'auto', maxHeight: '500px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <div
                        ref={containerRef}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        style={{
                          position: 'relative',
                          width: pageDimensions.width,
                          height: pageDimensions.height,
                          margin: '0 auto',
                          backgroundImage: `url(${pagePreview})`,
                          backgroundSize: 'contain',
                          cursor: draggingLogo ? 'grabbing' : 'default',
                        }}
                      >
                        {currentPageLogos.map((logo) => (
                          <div
                            key={logo.id}
                            onMouseDown={(e) => handleMouseDown(e, logo)}
                            style={{
                              position: 'absolute',
                              left: logo.x,
                              top: logo.y,
                              width: logo.width,
                              height: logo.height,
                              cursor: draggingLogo?.id === logo.id ? 'grabbing' : 'grab',
                              border: selectedLogo === logo.id ? '2px solid #FF9900' : '2px dashed #1890ff',
                              boxSizing: 'border-box',
                              opacity: logo.opacity / 100,
                            }}
                          >
                            <img
                              src={logo.src}
                              alt=""
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              draggable={false}
                            />
                            {/* Resize Handles */}
                            {selectedLogo === logo.id && (
                              <>
                                {['nw', 'ne', 'sw', 'se'].map((corner) => (
                                  <div
                                    key={corner}
                                    onMouseDown={(e) => handleResizeStart(e, logo, corner)}
                                    style={{
                                      position: 'absolute',
                                      width: '12px',
                                      height: '12px',
                                      backgroundColor: '#FF9900',
                                      border: '2px solid #fff',
                                      borderRadius: '2px',
                                      cursor: `${corner}-resize`,
                                      ...(corner.includes('n') ? { top: '-6px' } : { bottom: '-6px' }),
                                      ...(corner.includes('w') ? { left: '-6px' } : { right: '-6px' }),
                                    }}
                                  />
                                ))}
                                {['n', 's', 'e', 'w'].map((edge) => (
                                  <div
                                    key={edge}
                                    onMouseDown={(e) => handleResizeStart(e, logo, edge)}
                                    style={{
                                      position: 'absolute',
                                      backgroundColor: '#FF9900',
                                      border: '1px solid #fff',
                                      borderRadius: '2px',
                                      cursor: `${edge === 'n' || edge === 's' ? 'ns' : 'ew'}-resize`,
                                      ...(edge === 'n' && { top: '-5px', left: '50%', transform: 'translateX(-50%)', width: '10px', height: '10px' }),
                                      ...(edge === 's' && { bottom: '-5px', left: '50%', transform: 'translateX(-50%)', width: '10px', height: '10px' }),
                                      ...(edge === 'e' && { right: '-5px', top: '50%', transform: 'translateY(-50%)', width: '10px', height: '10px' }),
                                      ...(edge === 'w' && { left: '-5px', top: '50%', transform: 'translateY(-50%)', width: '10px', height: '10px' }),
                                    }}
                                  />
                                ))}
                              </>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removePlacedLogo(logo.id);
                              }}
                              style={{
                                position: 'absolute',
                                top: -10,
                                right: -10,
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#ff4d4f',
                                color: '#fff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '14px',
                                lineHeight: '1',
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Panel - Settings */}
              {selectedLogoData && (
                <div style={{ width: '220px', flexShrink: 0 }}>
                  <div className="bordered-box">
                    <h6 className="container-header">Logo Settings</h6>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
                        Opacity
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="range"
                          min="10"
                          max="100"
                          value={selectedLogoData.opacity}
                          onChange={(e) => updateLogo(selectedLogo, { opacity: parseInt(e.target.value) })}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '12px', minWidth: '35px' }}>{selectedLogoData.opacity}%</span>
                      </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>
                        Size
                      </label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          value={selectedLogoData.width}
                          onChange={(e) => updateLogo(selectedLogo, { width: parseInt(e.target.value) || 50 })}
                          style={{ width: '60px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <span>×</span>
                        <input
                          type="number"
                          value={selectedLogoData.height}
                          onChange={(e) => updateLogo(selectedLogo, { height: parseInt(e.target.value) || 50 })}
                          style={{ width: '60px', padding: '4px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '8px' }}>
                        Apply To Pages
                      </label>
                      {[
                        { value: 'all', label: 'All Pages' },
                        { value: 'current', label: 'Current Page Only' },
                        { value: 'custom', label: 'Custom Range' },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          <input
                            type="radio"
                            name={`applyTo-${selectedLogo}`}
                            value={opt.value}
                            checked={selectedLogoData.applyTo === opt.value}
                            onChange={() => updateLogo(selectedLogo, { applyTo: opt.value })}
                          />
                          {opt.label}
                        </label>
                      ))}
                      {selectedLogoData.applyTo === 'custom' && (
                        <input
                          type="text"
                          placeholder="e.g., 1-3, 5, 7-9"
                          value={selectedLogoData.customRange}
                          onChange={(e) => updateLogo(selectedLogo, { customRange: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '12px',
                            marginTop: '4px',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Step 4: Success */
            <div
              className="bordered-box"
              style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center', padding: '40px' }}
            >
              <div style={{ fontSize: '30px', marginBottom: '16px' }}>✅</div>
              <h3 style={{ marginBottom: '8px', color: '#52c41a' }}>Logos Added Successfully!</h3>
              <p style={{ color: '#666', marginBottom: '24px' }}>Your branded PDF is ready to download.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={downloadResult}>
                  📥 Download PDF
                </Button>
                <Button variant="normal" onClick={() => setStep(3)}>
                  ✏️ Edit Again
                </Button>
                <Button variant="link" onClick={resetState}>
                  🔄 Start Over
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AddLogoToPdf;
